import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { FilingReportItem } from "../../../api";
import FilingReportAddStatusCell from "./FilingReportAddStatusCell";
import { FilingReportNoteCell } from "./FilingReportNoteCell";
import FilingReportStatusHistoryCell from "./FilingReportStatusHistoryCell";
import {
  ATTENTION_ROW_BG,
  ATTENTION_ROW_HOVER_BG,
  COLUMN_COUNT,
  HEAD_CELLS,
  OVERDUE_ROW_BG,
  ROW_NUMBER_WIDTH_PCT,
  STATUS_ATTENTION_DAYS,
  TABLE_BODY_CELL_SX,
  TABLE_HEADER_CELL_SX,
} from "./constants";

type FilingReportTableProps = {
  loading: boolean;
  hasLoadedData: boolean;
  uniqueCaseCount: number;
  taskCount: number;
  rows: FilingReportItem[];
  page: number;
  rowsPerPage: number;
  savingTaskId: number | null;
  savingNoteTaskId: number | null;
  onAddStatus: (row: FilingReportItem, status: string) => void;
  onSaveNote: (row: FilingReportItem, note: string) => Promise<void>;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  return dayjs(value).format("MM/DD/YYYY");
}

function isOverdue(dueDate?: string | null) {
  return !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");
}

function needsStatusAttention(row: FilingReportItem) {
  const referenceDate = row.statusUpdatedAt ?? row.createdAt;
  if (!referenceDate) return false;
  return dayjs().diff(dayjs(referenceDate), "day") >= STATUS_ATTENTION_DAYS;
}

export function FilingReportTable({
  loading,
  hasLoadedData,
  uniqueCaseCount,
  taskCount,
  rows,
  page,
  rowsPerPage,
  savingTaskId,
  savingNoteTaskId,
  onAddStatus,
  onSaveNote,
  onPageChange,
  onRowsPerPageChange,
}: FilingReportTableProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "rgba(15, 23, 42, 0.12)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      {hasLoadedData && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ px: 2, py: 1.25, bgcolor: "grey.50" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`${uniqueCaseCount} case${uniqueCaseCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
              <Chip
                size="small"
                label={`${taskCount} task${taskCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Rows needing a status update (≥{STATUS_ATTENTION_DAYS} days) are highlighted
            </Typography>
          </Stack>
          <Divider />
        </>
      )}

      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-stickyHeader": {
              boxShadow: "0 1px 0 rgba(15, 23, 42, 0.12)",
            },
          }}
        >
          <colgroup>
            <col style={{ width: `${ROW_NUMBER_WIDTH_PCT}%` }} />
            {HEAD_CELLS.map((headCell) => (
              <col key={headCell.id} style={{ width: `${headCell.widthPct}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: `${ROW_NUMBER_WIDTH_PCT}%` }}>
                #
              </TableCell>
              {HEAD_CELLS.map((headCell) => (
                <TableCell key={headCell.id} sx={TABLE_HEADER_CELL_SX}>
                  {headCell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 10 }}>
                  <Stack spacing={1.5} alignItems="center">
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">
                      Loading filing tasks…
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                  <Stack spacing={0.75} alignItems="center">
                    <Typography variant="subtitle2" color="text.primary">
                      {hasLoadedData ? "No matching tasks" : "No report loaded"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasLoadedData
                        ? "Try adjusting or clearing filters."
                        : "Click Load Report to load filing tasks."}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const needsAttention = needsStatusAttention(row);
                const overdue = isOverdue(row.dueDate);
                const rowNumber = page * rowsPerPage + index + 1;
                const cellBg = needsAttention
                  ? ATTENTION_ROW_BG
                  : overdue
                    ? OVERDUE_ROW_BG
                    : undefined;
                const cellSx = {
                  ...TABLE_BODY_CELL_SX,
                  ...(cellBg ? { bgcolor: cellBg } : {}),
                };

                return (
                  <TableRow
                    key={row.taskId}
                    hover
                    sx={{
                      "&.MuiTableRow-hover:hover": { bgcolor: "transparent" },
                      "&:hover .filing-cell": {
                        bgcolor: needsAttention
                          ? ATTENTION_ROW_HOVER_BG
                          : "rgba(241, 245, 249, 0.9)",
                      },
                    }}
                  >
                    <TableCell className="filing-cell" sx={{ ...cellSx, color: "text.secondary", fontWeight: 500 }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(15, 23, 42, 0.06)",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                        }}
                      >
                        {rowNumber}
                      </Box>
                    </TableCell>
                    <TableCell className="filing-cell" sx={{ ...cellSx, fontWeight: 600 }}>
                      {row.caseName}
                    </TableCell>
                    <TableCell className="filing-cell" sx={cellSx}>
                      {row.taskName}
                    </TableCell>
                    <TableCell className="filing-cell" sx={cellSx}>
                      {row.practiceArea ?? "—"}
                    </TableCell>
                    <TableCell className="filing-cell" sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell
                      className="filing-cell"
                      sx={{
                        ...cellSx,
                        whiteSpace: "nowrap",
                        color: overdue ? "error.dark" : "inherit",
                        fontWeight: overdue ? 700 : 500,
                      }}
                    >
                      {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                    </TableCell>
                    <TableCell className="filing-cell" sx={{ ...cellSx, whiteSpace: "pre-line" }}>
                      {row.caseTeam || "—"}
                    </TableCell>
                    <TableCell className="filing-cell" sx={cellSx}>
                      <FilingReportAddStatusCell
                        needsAttention={needsAttention}
                        saving={savingTaskId === row.taskId}
                        onAddStatus={(status) => onAddStatus(row, status)}
                      />
                    </TableCell>
                    <TableCell className="filing-cell" sx={cellSx}>
                      <FilingReportStatusHistoryCell row={row} />
                    </TableCell>
                    <TableCell className="filing-cell" sx={cellSx}>
                      <FilingReportNoteCell
                        value={row.note}
                        saving={savingNoteTaskId === row.taskId}
                        onSave={(note) => onSaveNote(row, note)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={taskCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        sx={{ flexShrink: 0, minHeight: 52 }}
      />
    </Paper>
  );
}
